/**
 * ============================================================================
 * usePostActionsQuery — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the post-action queue of a Change Control.
 *          After a CC is approved, the CCIE enqueues a side-effect action per
 *          impact item (training task creation, requalification trigger,
 *          regulatory notification, …). This hook polls the queue every 5 s
 *          so the UI tracks progress without a websocket connection.
 *
 * Endpoint: GET /api/v1/changes/{id}/post-actions
 *
 * NOTE: We intentionally re-fetch even when the page is hidden so a
 *  long-running batch finishes correctly. `refetchIntervalInBackground` is
 *  TanStack's idiom for that.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type PostActionStatus =
  | 'QUEUED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED';

export type PostActionType =
  | 'CREATE_TRAINING_TASK'
  | 'TRIGGER_REQUALIFICATION'
  | 'NOTIFY_REGULATORY'
  | 'INVALIDATE_VALIDATION'
  | 'NOTIFY_SUPPLIER'
  | 'UPDATE_DOCUMENT_OWNER'
  | 'PROPAGATE_BATCH_RECORD'
  | 'OTHER';

export interface PostAction {
  action_id: string;
  action_type: PostActionType;
  target_entity_id: string;
  target_entity_kind: string;
  target_display_name: string;
  status: PostActionStatus;
  queued_at_utc: string;
  started_at_utc: string | null;
  completed_at_utc: string | null;
  retry_count: number;
  /** When status === 'FAILED', a server-supplied short reason. */
  failure_reason: string | null;
  /** Arbitrary per-action extras — never use `any`. */
  payload: Record<string, unknown>;
}

export interface PostActionPipelineCounts {
  QUEUED: number;
  RUNNING: number;
  COMPLETED: number;
  FAILED: number;
}

export interface PostActionsResponse {
  change_id: string;
  pipeline: PostActionPipelineCounts;
  items: PostAction[];
  generated_at_utc: string;
}

export function usePostActionsQuery(
  changeId: string,
): UseQueryResult<PostActionsResponse> {
  return useQuery<PostActionsResponse>({
    queryKey: ['changes', changeId, 'post-actions'],
    enabled: !!changeId,
    queryFn: ({ signal }) =>
      apiFetch<PostActionsResponse>(
        `/api/v1/changes/${encodeURIComponent(changeId)}/post-actions`,
        { signal },
      ),
    // Real-time refresh — short cadence keeps the pipeline graph honest as
    // queue workers drain items.
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
  });
}
